'use client'

import { Content } from 'fukku/Dialog/Content/Content'
import { Footer } from 'fukku/Dialog/Footer/Footer'
import { Header } from 'fukku/Dialog/Header/Header'
import { cx } from 'utils/cx'
import { ChatDialogCloseConfirm } from '../ChatDialog/ChatDialogCloseConfirm/ChatDialogCloseConfirm'
import { ChatHeader } from '../ChatDialog/ChatHeader/ChatHeader'
import { ChatModalCloseConfirm } from '../ChatDialog/ChatModalCloseConfirm/ChatModalCloseConfirm'

import type { DialogAPI } from 'fukku/Dialog/Dialog'
import { Modal } from 'fukku/Dialog/Modal/Modal'
import { NonModal } from 'fukku/Dialog/NonModal/NonModal'
import { useLabels } from 'labels/hooks/useLabels/useLabels'
import { useEffect, useRef, useState } from 'react'
import { useResponsiveBehavior } from 'responsive/hooks/useResponsiveBehavior/useResponsiveBehavior'
import { sendCloseChatbotEvent } from '../../../analytics/sendCloseChatbotEvent'
import { IS_CHAT_OPEN_KEY } from '../../../constants/constants'
import { useChatBotMessages } from '../../../hooks/useChatBotMessages'
import { useChatbotDialog } from '../../../hooks/useChatbotDialog'
import { ChatDialogContent } from './ChatDialogContent/ChatDialogContent'
import { ChatDialogFooter } from './ChatDialogFooter/ChatDialogFooter'

import { useLayoutLayers } from 'hooks/useLayoutLayers/useLayoutLayers'
import { useResponsive } from 'responsive/hooks/useResponsive/useResponsive'
import styles from './ChatDialog.module.scss'

export function ChatDialog() {
	const { t } = useLabels()
	const { availableScreenSize, isSmall } = useResponsive()
	const { application } = useLayoutLayers()

	const { setIsDialogOpen, isDialogOpen } = useChatbotDialog()
	const { clearMessages, initChatBot } = useChatBotMessages()

	const [isExitOpen, setIsExitOpen] = useState(false)

	const dialogRef = useRef<DialogAPI>(null)

	const ResponsiveDialog = useResponsiveBehavior({
		default: NonModal,
		small: Modal,
	})

	const isModal = useResponsiveBehavior<boolean>({
		default: false,
		small: true,
	})

	const onPreventClose = () => {
		setIsExitOpen(true)
	}
	const onMinimizeDialog = () => {
		setIsDialogOpen(false)
		localStorage.setItem(IS_CHAT_OPEN_KEY, 'false')
		dialogRef.current?.close()
	}

	const onCancelLeave = () => {
		setIsExitOpen(false)
	}

	const onLeave = () => {
		clearMessages()
		setIsDialogOpen(false)
		setIsExitOpen(false)
		localStorage.setItem(IS_CHAT_OPEN_KEY, 'false')
		dialogRef.current?.close()
		sendCloseChatbotEvent()
	}

	useEffect(() => {
		if (isDialogOpen) {
			dialogRef.current?.show()
		}
	}, [isDialogOpen])

	useEffect(() => {
		if (availableScreenSize && isDialogOpen) {
			setIsDialogOpen(false)

			if (!isSmall) {
				application?.removeAttribute('inert')
			}
		}
	}, [availableScreenSize, isSmall, application])

	useEffect(() => {
		const hadChatDialogOpen = localStorage.getItem(IS_CHAT_OPEN_KEY) === 'true'

		if (hadChatDialogOpen) {
			setIsDialogOpen(true)
			initChatBot()
		}
	}, [])

	return (
		<ResponsiveDialog
			location={{
				bottom: '0px',
				right: '0px',
			}}
			className={cx(!isModal && styles.chatDialog)}
			title={t('assistant.header.title')}
			ref={dialogRef}
			preventClose
			isFullScreen
		>
			<Header
				title={t('assistant.header.title')}
				alternativeAction={
					<ChatHeader
						onMinimizeDialog={onMinimizeDialog}
						onPreventClose={onPreventClose}
					/>
				}
			/>
			<Content hasPadding={false}>
				<ChatDialogContent />
			</Content>
			<Footer>
				<ChatDialogFooter />
			</Footer>
			{isExitOpen &&
				(isModal ? (
					<ChatModalCloseConfirm
						isExitOpen={isExitOpen}
						onCancelLeave={onCancelLeave}
						onLeave={onLeave}
					/>
				) : (
					<ChatDialogCloseConfirm
						onCancelLeave={onCancelLeave}
						onLeave={onLeave}
					/>
				))}
		</ResponsiveDialog>
	)
}
